// ============================================================
// Forgot Password Page
// ============================================================

import { useState } from 'react';
import { Card, CardContent, Typography, Button, Box, Alert } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { FormField } from '@/components/forms/FormField';
import authService from '@/services/authService';
import { tokens } from '@/theme/tokens';

const forgotSchema = Yup.object().shape({
  email: Yup.string()
    .email('Enter a valid email address')
    .required('Email is required'),
});

export function ForgotPasswordPage() {
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await authService.forgotPassword(values.email);
      setSuccess(true);
    } catch {
      // handled by service
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      sx={{
        boxShadow: tokens.shadows.lg,
        border: `1px solid ${tokens.colors.border}`,
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
          Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your email address and we'll send you a reset link
        </Typography>

        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Password reset link sent! Check your email inbox.
            </Alert>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              fullWidth
              startIcon={<ArrowLeft size={18} />}
            >
              Back to Login
            </Button>
          </Box>
        ) : (
          <Formik
            initialValues={{ email: '' }}
            validationSchema={forgotSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <FormField
                  name="email"
                  label="Email Address"
                  type="email"
                  placeholder="Enter your email"
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isSubmitting}
                  startIcon={<Mail size={18} />}
                  sx={{ mb: 2, py: 1.2 }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    component={Link}
                    to="/login"
                    variant="body2"
                    sx={{
                      color: tokens.colors.primary.main,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    <ArrowLeft size={14} />
                    Back to Login
                  </Typography>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </CardContent>
    </Card>
  );
}
